import { ReactNode } from 'react';
import { MessageDescriptor } from 'react-intl';

import { instanceOfString } from '@utils/typeGuards';

import { capitalize } from './capitalize';

/**
 * React Intl uses MessageId as a chunk text in case that message could not be constructed
 * This method checks if chunk contains MessageId as its value
 * - Null values are not going to be rendered in the resulting HTML
 * @param chunks - Array of react intl chunks
 * @param message - Message which is going to be checked
 * @return {ReactNodeArray | null} - If chunk is empty, null value is returned. If NOT original chunk array is returned.
 */
export const checkForEmptyMessage = (chunks: ReactNode[], message: MessageDescriptor) => {
  if (chunks?.length > 0 && chunks[0] === message.id) {
    return null;
  }
  return chunks;
};

/**
 * Parametrized version of checkForEmptyMessage.
 * React Intl uses MessageId as a chunk text in case that message could not be constructed
 * This method checks if chunk contains MessageId as its value
 * - Null values are not going to be rendered in the resulting HTML
 * @param param - React intl message parameter
 * @param chunks - Array of react intl chunks
 * @param message - Message which is going to be checked
 * @return {ReactNodeArray | null} - If chunk is empty, null value is returned. If NOT original chunk array is returned.
 */
export const checkForEmptyMessageWithParams = <TParam>(
  chunks: ReactNode[],
  message: MessageDescriptor,
  param: TParam,
) => {
  if (!param) {
    return null;
  }
  return checkForEmptyMessage(chunks, message);
};

/**
 * if first chunk is a string capitalize first letter
 * @param chunks - Array of react intl chunks
 */
export const capitalizeFirstChunk = (chunks: ReactNode[]) => {
  if (instanceOfString(chunks[0])) {
    chunks[0] = capitalize(chunks[0].trimStart());
  }
  return chunks;
};

/**
 * remove colon from the last chunk
 * @param chunks - Array of react intl chunks
 */
export const removeColonFromLastChunk = (chunks: ReactNode[]) => {
  const lastChunkValue = chunks[chunks.length - 1];

  if (instanceOfString(lastChunkValue)) {
    chunks[chunks.length - 1] = lastChunkValue.trimEnd().replace(/,$/, '');
  }

  return chunks;
};

/**
 * remove colon from end of the chunk
 * @param chunks - Array of react intl chunks
 */
export const removeColonFromEndOfChunk = (chunks: ReactNode | ReactNode[]) => {
  if (instanceOfString(chunks)) {
    return chunks[chunks.length - 1].replace(',', '');
  }

  if (Array.isArray(chunks)) {
    const lastChunk = chunks[chunks.length - 1] as unknown;
    if (instanceOfString(lastChunk)) {
      chunks[chunks.length - 1] = lastChunk.replace(',', '');
    }
  }

  return chunks;
};

/**
 * remove double spaces from string
 * @param chunks - Array of react intl chunks
 */
export const removeDuplicateSpacesInString = (chunks: ReactNode[]) =>
  chunks.map((chunk) => {
    if (instanceOfString(chunk)) {
      return chunk.replace(/\s+/g, ' ');
    }

    return chunk;
  });
